import React from "react";
import lodash from 'lodash';
import { Stack } from "@mui/material";
import { AlphaIcon } from "./svg/alpha";
import { usePlans } from "@/hooks/usePlans";
import { SearchIcon } from "./svg/searchIcon";
import { CalendarIcon } from "./svg/calendar";
import { useTheme } from "@/services/zustand/theme";
import { removeArticles } from "@/utils/removeArticles";
import { DownArrowIcon, UpArrowIcon } from "./svg/arrows";
import { Container, MobileFilterWrapper } from "./styles";
import { useAccessibility } from "@/hooks/useAccessibility";
import { useMediaQuery, query } from "@/hooks/useMediaQuery";
import { AddSvgToData } from "@/utils/SvgDictionaries/addSvgToData";
import { ReseachInput } from "@/components/global/Inputs/ResearchInput";
import { FloatingFilterButton } from "@/components/global/FloatingButton";
import { PlansService } from "@/services/endpoints/plans";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled as MuiStyled, ThemeProvider, createTheme } from "@mui/material/styles";
import { DesktopFilterPlans } from "./desktopFilter";
import { shuffleArray } from "@/utils/shuffleArray";
import CloseIcon from '@mui/icons-material/Close';

const MaterialTheme = createTheme({
  palette: {
    primary: {
      main: "#505A5F",
    },
    secondary: {
      main: "#FFFFFF",
    },
  },
});

export const BootstrapTooltip = MuiStyled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "#FDF6DA",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#FDF6DA",
    color: "rgba(0, 0, 0, 0.87)",
    padding: "1rem",
    maxWidth: 220,
    boxShadow: "5px 6px 11px 0px rgba(0, 0, 0, 0.16)",
  },
}));

export const FilterPlans: React.FC = () => {
  const {
    plans,
    setPlans,
    isAlphaOrder,
    isFinalOrder,
    isInitialOrder,
    setIsFinalOrder,
    setIsAlphaOrder,
    searchJsonTerms,
    handleSearchTerm,
    setIsInitialOrder,
    inputResearchText,
    handleGetAllPlans,
    orderPlansByAlpha,
    setInputResearchText,
    orderPlansByFinalYear,
    orderPlansByInitialYear,
  } = usePlans();

  const { isMobile } = useMediaQuery(query);
  const { getClassificationPlans } = new PlansService();
  const theme = useTheme((state) => state.theme);
  const { monitoringFloatingButtonRef } = useAccessibility();

  const handleTextChange = (text: string) => {
    handleSearchTerm({ query: text });
  };

  React.useEffect(() => {
    if (inputResearchText.length <= 0) {
      delete searchJsonTerms["query"];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputResearchText]);

  React.useEffect(() => {
    if (isInitialOrder) {
      orderPlansByInitialYear();
    }

    if (isFinalOrder) {
      orderPlansByFinalYear();
    }

    if (!isInitialOrder && !isFinalOrder && isAlphaOrder) {
      const data = { sort: 'ASC', page: '1', size: String(plans.length) };
      getClassificationPlans(data).then(res => {
        const { data } = res.data as any;
        const dataSvg = AddSvgToData(data);
        const it = dataSvg?.map((item: any) => ({
          ...item,
          // orderAlpha: removeArticles(item?.titulo),
        }));
        const palavrasOrdenadas = it.sort((a: any, b: any) => {
          return a.titulo.localeCompare(b.titulo, undefined, {
            sensitivity: 'base',
            ignorePunctuation: true,
          });
        });
        setPlans(palavrasOrdenadas);
      });
    }

    if ((!isInitialOrder && !isAlphaOrder) && (Object.keys(searchJsonTerms).length > 0)) {
      const orderArray = lodash.orderBy(plans as any[], [i => i?.hits?.length], ['desc']);
      setPlans(orderArray);
      return;
    }

    if (!isInitialOrder && !isFinalOrder && !isAlphaOrder) {
      const newArray = lodash.orderBy(plans, ["id"], ["asc"]);
      setPlans(newArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialOrder, isFinalOrder]);

  React.useEffect(() => {
    orderPlansByAlpha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAlphaOrder]);

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      handleTextChange(inputResearchText);
    }
  };

  const handleOnChange = (e: any) => {
    setInputResearchText(e.target.value);
    if (e.target.value?.length === 0) {
      handleGetAllPlans(1);
    }
  };

  return (
    <Container ref={monitoringFloatingButtonRef}>
      <div className="auto_complete_wrapper">
        <Stack sx={{ width: "100%" }}>
          <ThemeProvider theme={MaterialTheme}>
            <ReseachInput
              onKeyDown={handleKeyDown}
              onChange={handleOnChange}
              value={inputResearchText ?? ""}
              color={theme === "light" ? "primary" : "secondary"}
              InputProps={{
                style: { color: theme === "light" ? "#505A5F" : "#ffffff" }, 
                endAdornment: inputResearchText.length > 0 && ( 
                  <CloseIcon 
                    sx={{ cursor: 'pointer' }} 
                    onClick={() =>  {
                      setInputResearchText('');
                      handleTextChange('')
                      handleGetAllPlans(1);
                    }} 
                  /> 
                )
              }}
            />
          </ThemeProvider>
        </Stack>
        <button
          className="auto_complete_button"
          onClick={() => handleTextChange(inputResearchText)}
        >
          <SearchIcon />
        </button>
      </div>
      <section className="order_date_wrapper">
        {isMobile && (
          <MobileFilterWrapper>
            <FloatingFilterButton />
            <div className="alpha" onClick={() => setIsAlphaOrder(!isAlphaOrder)}>
              <AlphaIcon isClicked={isAlphaOrder} />
            </div>
            <div style={{ display: "flex" }}>
              <div
                className="down"
                onClick={() => {
                  setIsFinalOrder(false);
                  setIsInitialOrder(!isInitialOrder);
                }}
                style={{ background: isInitialOrder ? "#004A80" : "" }}
              >
                <CalendarIcon isClicked={isInitialOrder} />
                <DownArrowIcon isClicked={isInitialOrder} />
              </div>
              <div
                className="up"
                onClick={() => {
                  setIsInitialOrder(false);
                  setIsFinalOrder(!isFinalOrder);
                }}
                style={{ background: isFinalOrder ? "#004A80" : "" }}
              >
                <CalendarIcon isClicked={isFinalOrder} />
                <UpArrowIcon isClicked={isFinalOrder} />
              </div>
            </div>
          </MobileFilterWrapper>
        )}
        {!isMobile && (<DesktopFilterPlans />)}
      </section>
    </Container>
  );
};
